import type React from 'react'
import type { JSX } from 'react'
import type { TextBoxColors } from 'atoms/TextBox'
import type { BANNER_ASPECT_RATIO } from 'vars/banner-aspect-ratio'

import { useCallback, useMemo, useRef } from 'react'
import { BannerSkeleton } from 'blocks/Skeletons/components/BannerSkeleton/BannerSkeleton'
import { BANNER_WIDTH } from 'vars/banner-width'
import { BannerPagination } from './components'
import {
  getBannerContentIndexFromSlide,
  resolveBannerSlideIndex,
  useBannerAdaptiveWidth,
  useBannerElements,
  useBannerRowScrollTransition,
  useBannerScroll,
  useSwipeBanner,
} from './hooks'
import { CellsAlignment, Gaps, RowControlsType } from './RowContainer.h'
import {
  BannerCarouselEmptySC,
  BannerCarouselSC,
  BannerRowSC,
} from './RowContainer.styled'

export interface IBannerRowContainerProps {
  setRef?: (node?: Element | null | undefined) => void
  width?: BANNER_WIDTH
  gap?: (typeof Gaps)[keyof typeof Gaps]
  children: React.ReactNode
  controlsType?: RowControlsType
  cellsAlignment?: CellsAlignment
  infiniteLoop?: boolean
  autoScrollEnabled: boolean
  autoScrollIn?: number
  color?: TextBoxColors
  itemCount?: number
  aspectRatio?: BANNER_ASPECT_RATIO
  render?: (i: number) => void
  isPopupOpen?: boolean
  loaded?: boolean
  childrenLength: number
  onSlideChanged?: (slide: number, contentIndex: number | null) => void
  dataTestIdBannersCarousel?: string
  /** Trigger to programmatically go to next slide (incremented from outside) */
  goToNextSlideTrigger?: number
  /** Restored active slide index for browser back navigation */
  initialSlide?: number
}

export const BannerRowContainer = ({
  setRef,
  width = BANNER_WIDTH.SIZE_100,
  aspectRatio,
  gap = Gaps.SIZE_40,
  children,
  controlsType = RowControlsType.inRowFluid,
  cellsAlignment = CellsAlignment.Center,
  infiniteLoop = true,
  autoScrollEnabled = true,
  autoScrollIn,
  color,
  isPopupOpen,
  loaded,
  childrenLength,
  onSlideChanged,
  dataTestIdBannersCarousel,
  goToNextSlideTrigger,
  initialSlide,
}: IBannerRowContainerProps): JSX.Element => {
  const { adaptiveWidth, visibleCards } = useBannerAdaptiveWidth({
    width,
    childrenLength,
  })

  const bannerRef = useRef<HTMLDivElement>(null)

  const { prevFn, nextFn, goToContentIndex, activeSlide, setActiveSlide } =
    useBannerScroll({
      length: childrenLength,
      visibleCards,
      infiniteLoop,
      autoScrollIn,
      autoScrollEnabled,
      isPopupOpen,
      onSlideChanged,
      goToNextSlideTrigger,
      initialSlide,
    })

  useSwipeBanner({ bannerRef, prevFn, nextFn })

  const { banners } = useBannerElements({
    visibleCards,
    infiniteLoop,
    children,
    childrenLength,
  })

  const { scrollEnabled, handleTransitionEnd, disableTransitionIfJump } =
    useBannerRowScrollTransition({
      infiniteLoop,
      activeSlide,
      visibleCards,
      setActiveSlide,
      length: childrenLength,
    })

  const handlePaginationSelect = (contentIndex: number): void => {
    disableTransitionIfJump(
      activeSlide,
      resolveBannerSlideIndex(contentIndex, infiniteLoop, visibleCards)
    )
    goToContentIndex(contentIndex)
  }

  const handleRef = useCallback(
    (el) => {
      bannerRef.current = el
      setRef?.(el)
    },
    [setRef]
  )

  const activeContentIndex = useMemo(
    () =>
      getBannerContentIndexFromSlide(
        activeSlide,
        childrenLength,
        visibleCards,
        infiniteLoop
      ) ?? 0,
    [activeSlide, childrenLength, visibleCards, infiniteLoop]
  )

  if (!loaded) {
    return (
      <BannerSkeleton
        ref={setRef}
        aspectRatio={aspectRatio}
        bannerWidth={adaptiveWidth}
      />
    )
  }

  if (loaded && !childrenLength) {
    return <BannerCarouselEmptySC ref={setRef} />
  }

  const showPagination = childrenLength > visibleCards

  return (
    <BannerCarouselSC
      controlsType={controlsType}
      bannerWidth={adaptiveWidth}
      cellsAlignment={cellsAlignment}
      gap={gap}
      colorDefault={color}
      aspectRatio={aspectRatio}
      data-has-pagination={showPagination || undefined}
      data-testid={dataTestIdBannersCarousel}
    >
      <BannerRowSC
        bannerWidth={adaptiveWidth}
        gap={adaptiveWidth === BANNER_WIDTH.SIZE_100 ? Gaps.SIZE_0 : gap}
        ref={handleRef}
        onTransitionEnd={handleTransitionEnd}
        activeSlide={activeSlide}
        cellsAlignment={cellsAlignment}
        scrollEnabled={scrollEnabled}
        infiniteLoop={infiniteLoop}
        visibleCards={visibleCards}
      >
        {banners || children}
      </BannerRowSC>
      {showPagination && (
        <BannerPagination
          count={childrenLength}
          activeIndex={activeContentIndex}
          onSelect={handlePaginationSelect}
          dataTestId={
            dataTestIdBannersCarousel &&
            `${dataTestIdBannersCarousel}-pagination`
          }
        />
      )}
    </BannerCarouselSC>
  )
}
